import { ROUTES } from '#/config/routes';
import {
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { formatDateOrDash } from '#/utils/date';
import { formatDocumentCurrencyLabel } from '#/utils/documentCurrencyLabel';

import MiscPurchaseOrderStatusBadge from '../components/MiscPurchaseOrderStatusBadge';
import {
  readMiscPurchaseOrderDateRangeFilter,
  resolveMiscPurchaseOrderDateFilterChipDisplay,
} from '../helper/dateListFilter';
import {
  MISC_PURCHASE_ORDER_STATUS_FILTER_OPTIONS,
} from '../helper/miscPurchaseOrderStatus';

function displayDash(value) {
  if (value == null || value === '') {
    return '-';
  }
  return value;
}

/** list／audit 常為 `YYYY-MM-DD HH:mm:ss`；取日部分顯示。 */
function formatListDate(value) {
  if (value == null || value === '') {
    return '-';
  }
  return formatDateOrDash(String(value).trim().slice(0, 10));
}

export function buildMiscPurchaseOrderColumns({
  filters = {},
  onFilterChange,
  onDateRangeChange,
  currencyOptions = [],
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.miscPurchaseOrderDetail(row.id),
      getEditPath: (row) => ROUTES.miscPurchaseOrderEdit(row.id),
      canShowEdit: (row) => row.status === 'DRAFT',
      size: 100,
      minSize: 100,
    }),
    {
      id: 'purchaseOrderNo',
      header: '採購單號',
      accessorKey: 'purchaseOrderNo',
      accessorFn: (row) => displayDash(row.purchaseOrderNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'purchaseOrderNo',
        label: '採購單號',
        filterValue: filters.purchaseOrderNo,
        onChange: (value) => onFilterChange('purchaseOrderNo', value),
        placeholder: '搜尋採購單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 120,
      minSize: 120,
      enableSorting: false,
      cell: ({ row }) => (
        row.original.status
          ? <MiscPurchaseOrderStatusBadge status={row.original.status} size="sm" />
          : '-'
      ),
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: MISC_PURCHASE_ORDER_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'supplierName',
      header: '供應商',
      accessorKey: 'supplierName',
      accessorFn: (row) => displayDash(row.supplierName),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'supplierName',
          label: '供應商',
          filterValue: filters.supplierName,
          onChange: (value) => onFilterChange('supplierName', value),
          placeholder: '搜尋供應商',
        }),
      },
    },
    {
      id: 'purchaseOrderDate',
      header: '採購日期',
      accessorKey: 'purchaseOrderDate',
      accessorFn: (row) => formatDateOrDash(row.purchaseOrderDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'purchaseOrderDateFrom',
        label: '採購日期',
        mode: 'range',
        filterValue: readMiscPurchaseOrderDateRangeFilter(filters),
        onChange: onDateRangeChange,
        placeholder: '西元年/月/日',
        resolveFilterDisplay: resolveMiscPurchaseOrderDateFilterChipDisplay,
      }),
    },
    {
      id: 'createdAt',
      header: '建立日期',
      accessorFn: (row) => formatListDate(row.record?.createdAt),
      size: 120,
      minSize: 120,
      enableSorting: false,
    },
    {
      id: 'closedAt',
      header: '結案日期',
      accessorFn: (row) => formatListDate(row.history?.closedAt),
      size: 120,
      minSize: 120,
      enableSorting: false,
    },
    {
      id: 'currencyCode',
      header: '幣別',
      accessorFn: (row) => formatDocumentCurrencyLabel(row),
      size: 160,
      minSize: 140,
      enableSorting: false,
      // be doc abbce90d（#63）：currency_id 逗號 OR；重複 key 400，fetchList 以逗號送
      enableColumnFilter: true,
      meta: createFixedMultiFilterMeta({
        key: 'currencyId',
        label: '幣別',
        options: currencyOptions,
        filterValue: filters.currencyId,
        onChange: (value) => onFilterChange('currencyId', value),
      }),
    },
    {
      id: 'issueReason',
      header: '事由',
      accessorKey: 'issueReason',
      accessorFn: (row) => displayDash(row.issueReason),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'issueReason',
          label: '事由',
          filterValue: filters.issueReason,
          onChange: (value) => onFilterChange('issueReason', value),
          placeholder: '搜尋事由',
        }),
      },
    },
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      accessorFn: (row) => displayDash(row.remark),
      size: 200,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />
      ),
    },
  ];
}

export const miscPurchaseOrderSideMsgBlocks = [
  {
    fields: [
      { label: '採購單號', value: 'purchaseOrderNo' },
      { label: '採購日期', value: 'purchaseOrderDate' },
      { label: '供應商', value: 'supplierName' },
      { label: '事由', value: 'issueReason' },
    ],
  },
];
